import React, { useState, useCallback, useEffect } from 'react';
import {
    Mode,
    ZoneInputs,
    DEFAULT_INPUTS_SBR,
    DEFAULT_INPUTS_CPDLPD,
    DEFAULT_INPUTS_INTRUDER_DETECTION,
    DEFAULT_OCCUPANCY_BOXES,
    computeZones,
    formatOutput,
    parseCLICommands,
    Cuboid,
    SensorPosition,
    OccupancyBox,
} from '../calculator';
import { buildConfigFile } from '../chirpConfigs';
import { ZoneVisualizer } from './ZoneVisualizer';
import { ZONE_MEASUREMENTS_IMAGE } from '../assets/zoneMeasurementsImage';

type InputKey = keyof Omit<ZoneInputs, 'mode'>;

interface FieldDef {
    key: InputKey;
    label: string;
    unit: string;
    step: number;
    tooltip?: string;
}

const SENSOR_FIELDS: FieldDef[] = [
    { key: 'sensorXOffset', label: 'Sensor lateral offset (xOffset)',     unit: 'm',   step: 0.01,
      tooltip: 'Horizontal offset of sensor from vehicle centerline. Positive = toward driver side.' },
    { key: 'yRef',          label: 'Sensor forward offset (yRef)',        unit: 'm',   step: 0.01,
      tooltip: 'Reference point on vehicle front (e.g., windshield). All Y measurements are relative to this.' },
    { key: 'y1',            label: 'Sensor depth relative to yRef (y1)',   unit: 'm',   step: 0.01,
      tooltip: 'Distance from yRef to sensor mounting position along vehicle length. Positive = sensor behind yRef.' },
    { key: 'sensorZOffset', label: 'Sensor height (zOffset)',              unit: 'm',   step: 0.01,
      tooltip: 'Vertical mounting height of sensor above vehicle floor/ground plane.' },
    { key: 'omega',         label: 'Sensor azimuth tilt (ω)',              unit: '°',   step: 1,
      tooltip: 'Horizontal rotation (yaw) of sensor. Positive = clockwise when viewed from above.' },
    { key: 'theta',         label: 'Sensor elevation tilt (θ)',            unit: '°',   step: 1,
      tooltip: 'Vertical tilt (pitch) of sensor. Negative = tilted downward toward cabin floor.' },
];

const VEHICLE_FIELDS: FieldDef[] = [
    { key: 'ceilingHeight',   label: 'Cabin ceiling height',              unit: 'm',   step: 0.01,
      tooltip: 'Distance from floor to ceiling/headliner. Defines upper bound of body zones.' },
    { key: 'legHeight',       label: 'Leg zone top height',               unit: 'm',   step: 0.01,
      tooltip: 'Height separating body zone (above) from leg zone (below). Typically seat cushion height + torso.' },
    { key: 'y2',              label: '2nd row seat back Y offset (y2)',    unit: 'm',   step: 0.01,
      tooltip: 'Distance from yRef to 2nd row seat back. Defines rear boundary of 2nd row body zone.' },
    { key: 'y3',              label: '2nd row seat front Y offset (y3)',   unit: 'm',   step: 0.01,
      tooltip: 'Distance from yRef to 2nd row seat front edge. Defines front boundary of 2nd row zones.' },
];

const SEAT1_FIELDS: FieldDef[] = [
    { key: 'seat1Height',        label: 'Row 1 seat height',              unit: 'm',   step: 0.01,
      tooltip: 'Height of front seat cushion from floor. Bottom of leg zone.' },
    { key: 'd1ChairSlope',       label: 'Row 1 seat slope',               unit: 'm',   step: 0.01,
      tooltip: 'Forward/backward seat cushion slope. Positive = front higher than rear.' },
    { key: 'seat1ChairDepth',    label: 'Row 1 chair depth',              unit: 'm',   step: 0.01,
      tooltip: 'Depth of seat cushion (front to back). Defines Y extent of body zone.' },
    { key: 'seat1LegRoom',       label: 'Row 1 leg room',                 unit: 'm',   step: 0.01,
      tooltip: 'Space between front seat back and dashboard/pedals. Depth of leg zone.' },
    { key: 'seat1FootwellDepth', label: 'Row 1 footwell depth',           unit: 'm',   step: 0.01,
      tooltip: 'Depth of footwell area under dashboard. Extends leg zone forward.' },
];

const SEAT2_FIELDS: FieldDef[] = [
    { key: 'seat2Height',        label: 'Row 2 seat height',              unit: 'm',   step: 0.01,
      tooltip: 'Height of 2nd row seat cushion from floor.' },
    { key: 'seat2ChairDepth',    label: 'Row 2 chair depth',              unit: 'm',   step: 0.01,
      tooltip: 'Depth of 2nd row seat cushion (front to back).' },
    { key: 'seat2LegRoom',       label: 'Row 2 leg room',                 unit: 'm',   step: 0.01,
      tooltip: 'Space between 2nd row seat back and front seat back.' },
    { key: 'seat2FootwellDepth', label: 'Row 2 footwell depth',           unit: 'm',   step: 0.01,
      tooltip: 'Depth of 2nd row footwell area under front seats.' },
];

const MARGIN_FIELDS: FieldDef[] = [
    { key: 'seatHeightExpandMargin',      label: 'Seat height expand margin',          unit: 'm', step: 0.01,
      tooltip: 'Extra margin added below seat height for body zone expansion.' },
    { key: 'seat2FootwellExpandMargin',   label: '2nd row footwell expand margin',     unit: 'm', step: 0.01,
      tooltip: 'Extra margin extending 2nd row footwell zone forward/backward.' },
    { key: 'seat2RearFacingExpandMargin', label: '2nd row rear-facing expand margin',  unit: 'm', step: 0.01,
      tooltip: 'Extra margin for rear-facing child seat scenarios in 2nd row.' },
];

const MIDDLE_FIELDS: FieldDef[] = [
    { key: 'm1', label: 'Middle leg yMin offset (m1)',    unit: 'm', step: 0.01,
      tooltip: 'Front boundary of middle seat leg zone relative to yRef.' },
    { key: 'm2', label: 'Middle leg/footwell trim (m2)',  unit: 'm', step: 0.01,
      tooltip: 'Trim adjustment between leg and footwell zones for middle seat.' },
    { key: 'm3', label: 'Middle leg/footwell zMin (m3)',  unit: 'm', step: 0.01,
      tooltip: 'Floor height for middle seat leg/footwell zones (often same as seat height).' },
    { key: 'm4', label: 'Middle body yMax offset (m4)',   unit: 'm', step: 0.01,
      tooltip: 'Rear boundary adjustment for middle seat body zone.' },
];

const SEAT_NAMES = [
    'Seat 0: Driver',
    'Seat 1: Passenger',
    'Seat 2: 2nd Row Driver',
    'Seat 3: Middle',
    'Seat 4: 2nd Row Passenger',
];
const ZONE_NAMES = ['Body', 'Leg', 'Footwell'];

function fmt3(v: number): string {
    return Number(v.toFixed(3)).toString();
}

function NumberInput({ fieldDef, value, onChange }: {
    fieldDef: FieldDef;
    value: number;
    onChange: (key: InputKey, val: number) => void;
}) {
    const [text, setText] = React.useState(() => String(value));
    const lastExternal = React.useRef(value);
    const isFocused    = React.useRef(false);
    const [showTooltip, setShowTooltip] = React.useState(false);

    /* Sync from parent only when not actively editing (e.g. Reset Defaults) */
    if (!isFocused.current && lastExternal.current !== value) {
        lastExternal.current = value;
        setText(String(value));
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setText(raw);
        const parsed = parseFloat(raw);
        if (!isNaN(parsed) && raw.trim() !== '') {
            lastExternal.current = parsed;
            onChange(fieldDef.key, parsed);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        /* Stop Backspace/Delete from reaching any NW.js/GC global handler
         * that calls preventDefault() to block browser back-navigation. */
        if (e.key === 'Backspace' || e.key === 'Delete') {
            e.stopPropagation();
            return;
        }
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        const current = parseFloat(text);
        if (isNaN(current)) return;
        const delta    = fieldDef.step * (e.key === 'ArrowUp' ? 1 : -1);
        const decimals = (String(fieldDef.step).split('.')[1] ?? '').length;
        const next     = parseFloat((current + delta).toFixed(decimals));
        setText(String(next));
        lastExternal.current = next;
        onChange(fieldDef.key, next);
    };

    return (
        <div className="field-row" onMouseEnter={() => fieldDef.tooltip && setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
            <label className="field-label">{fieldDef.label}</label>
            <input
                className="field-input"
                type="text"
                inputMode="decimal"
                value={text}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { isFocused.current = true; }}
                onBlur={() => {
                    isFocused.current = false;
                    /* Normalise display to the computed value on blur */
                    setText(String(value));
                    lastExternal.current = value;
                }}
            />
            <span className="field-unit">{fieldDef.unit}</span>
            {fieldDef.tooltip && showTooltip && (
                <div className="field-tooltip">{fieldDef.tooltip}</div>
            )}
        </div>
    );
}

function FieldGroup({ title, fields, inputs, onChange }: {
    title: string;
    fields: FieldDef[];
    inputs: ZoneInputs;
    onChange: (key: InputKey, val: number) => void;
}) {
    return (
        <div className="field-group">
            <h3 className="group-title">{title}</h3>
            {fields.map(f => (
                <NumberInput key={f.key} fieldDef={f} value={inputs[f.key] as number} onChange={onChange} />
            ))}
        </div>
    );
}

function SensorPositionRow({ sp }: { sp: SensorPosition }) {
    return (
        <tr className="sp-row">
            <td colSpan={2} className="output-label">sensorPosition</td>
            <td>{fmt3(sp.xOffset)}</td>
            <td>{fmt3(sp.yOffset)}</td>
            <td>{fmt3(sp.zOffset)}</td>
            <td>{fmt3(sp.azimTilt)}</td>
            <td>{fmt3(sp.elevTilt)}</td>
            <td>{'—'}</td>
        </tr>
    );
}

function CuboidRow({ c }: { c: Cuboid }) {
    return (
        <tr className={'seat-' + c.seatIdx}>
            <td>{SEAT_NAMES[c.seatIdx]}</td>
            <td>{ZONE_NAMES[c.zoneIdx]}</td>
            <td>{fmt3(c.xMin)}</td>
            <td>{fmt3(c.xMax)}</td>
            <td>{fmt3(c.yMin)}</td>
            <td>{fmt3(c.yMax)}</td>
            <td>{fmt3(c.zMin)}</td>
            <td>{fmt3(c.zMax)}</td>
        </tr>
    );
}

type BoxBoundKey = 'xMin' | 'xMax' | 'yMin' | 'yMax' | 'zMin' | 'zMax';
const BOX_BOUND_KEYS: BoxBoundKey[] = ['xMin', 'xMax', 'yMin', 'yMax', 'zMin', 'zMax'];

function OccupancyBoxRow({ box, onChange }: {
    box: OccupancyBox;
    onChange: (id: number, key: BoxBoundKey, val: number) => void;
}) {
    return (
        <tr>
            <td className="output-label">{box.label}</td>
            {BOX_BOUND_KEYS.map(key => (
                <td key={key}>
                    <input
                        className="box-table-input"
                        type="text"
                        inputMode="decimal"
                        defaultValue={String(box[key])}
                        onBlur={e => {
                            const parsed = parseFloat(e.target.value);
                            if (!isNaN(parsed)) {
                                onChange(box.id, key, parsed);
                            } else {
                                e.target.value = String(box[key]);
                            }
                        }}
                    />
                </td>
            ))}
        </tr>
    );
}

function defaultInputsForMode(m: Mode): ZoneInputs {
    switch (m) {
        case 'SBR':
            return DEFAULT_INPUTS_SBR;
        case 'CPDLPD':
            return DEFAULT_INPUTS_CPDLPD;
        case 'IntruderDetection':
            return DEFAULT_INPUTS_INTRUDER_DETECTION;
    }
}

// README modal content
const README_CONTENT = {
    title: 'Zone Definition Generator - Quick Start Guide',
    sections: [
        {
            title: 'Overview',
            content: 'This tool generates zone definitions (cuboidDef) for TI mmWave radar SBR (Seat Belt Reminder) and CPD/LPD (Child Presence Detection / Life Presence Detection) applications, plus occupancy boxes for Intruder Detection. Input your vehicle interior measurements and sensor mounting position to generate CLI configuration commands.'
        },
        {
            title: 'Measurement Reference',
            content: 'All measurements are in meters. The coordinate system: X = lateral (driver side positive), Y = longitudinal (rear positive from yRef), Z = vertical (up positive). yRef is a fixed reference point at the vehicle front (e.g., windshield cowl).'
        },
        {
            title: 'Sensor Position Measurements',
            items: [
                'sensorXOffset: Horizontal distance from vehicle centerline to sensor. Measure from center console to sensor mount.',
                'yRef: Fixed reference point at vehicle front. Typically windshield base or dashboard front edge.',
                'y1: Distance from yRef back to sensor along vehicle length. Positive = sensor behind yRef.',
                'sensorZOffset: Vertical height from floor to sensor mounting point.',
                'omega (azimuth): Sensor horizontal rotation. 0° = pointing straight forward. Positive = clockwise (toward driver).',
                'theta (elevation): Sensor vertical tilt. Negative = tilted down toward cabin. Typical: -60° to -120°.'
            ]
        },
        {
            title: 'Vehicle Dimensions',
            items: [
                'ceilingHeight: Floor to headliner. Upper bound of body zones.',
                'legHeight: Height separating body zone (torso) from leg zone. ~seat cushion + 0.3m.',
                'y2: yRef to 2nd row seat back. Rear boundary of 2nd row zones.',
                'y3: yRef to 2nd row seat front edge. Front boundary of 2nd row zones.'
            ]
        },
        {
            title: 'Seat Dimensions (Row 1 & Row 2)',
            items: [
                'seatHeight: Floor to seat cushion top. Bottom of leg zone.',
                'chairDepth: Seat cushion front-to-back depth. Y-extent of body zone.',
                'legRoom: Space from seat back to obstruction (dash or front seat). Leg zone depth.',
                'footwellDepth: Extension under dash/front seat. Extends leg zone forward.'
            ]
        },
        {
            title: 'Margins & Middle Seat Tuning',
            items: [
                'Expand margins add extra coverage for edge cases (rear-facing seats, footwell reach).',
                'Middle seat parameters (m1-m4) fine-tune the center position zones independently.'
            ]
        },
        {
            title: 'Workflow',
            items: [
                '1. Select mode: SBR, CPD/LPD, or Intruder Detection',
                '2. Enter measurements in left panel (hover labels for tips)',
                '3. View 3D visualization on right - drag sensor to adjust position',
                '4. Copy CLI commands from bottom panel for radar configuration',
                '5. Or paste existing CLI commands in "Load CLI Commands" to import'
            ]
        },
        {
            title: 'CLI Commands',
            items: [
                'sensorPosition xOffset yOffset zOffset azimTilt elevTilt',
                'cuboidDef seatIdx zoneIdx xMin xMax yMin yMax zMin zMax (SBR/CPD)',
                'occupancyBox id xMin xMax yMin yMax zMin zMax (Intruder)'
            ]
        },
        {
            title: '3D Plot Navigation',
            items: [
                'Click and hold to rotate',
                'Ctrl + click and hold to pan',
                'Scroll to zoom in and out'
            ]
        }
    ]
};

export function App() {
    const [mode, setMode] = useState<Mode>('SBR');
    const [inputs, setInputs] = useState<ZoneInputs>(DEFAULT_INPUTS_SBR);
    const [occupancyBoxes, setOccupancyBoxes] = useState<OccupancyBox[]>(DEFAULT_OCCUPANCY_BOXES);
    const [customCuboids, setCustomCuboids] = useState<Cuboid[]>([]);
    const [copied, setCopied] = useState(false);
    const [hasCLICuboids, setHasCLICuboids] = useState(false);
    const [showReadme, setShowReadme] = useState(true);
    const [showMeasurementImage, setShowMeasurementImage] = useState(false);

    useEffect(() => {
        // Show README on first load (could check localStorage for "don't show again")
        setShowReadme(true);
    }, []);

    const handleModeChange = useCallback((newMode: Mode) => {
        setMode(newMode);
        setInputs(defaultInputsForMode(newMode));
        setCustomCuboids([]); // Clear custom cuboids on mode change
        setHasCLICuboids(false);
        if (newMode === 'IntruderDetection') {
            setOccupancyBoxes(DEFAULT_OCCUPANCY_BOXES);
        }
    }, []);

    const handleChange = useCallback((key: InputKey, val: number) => {
        setInputs(prev => ({ ...prev, [key]: val }));
    }, []);

    const handleBoxChange = useCallback((id: number, key: BoxBoundKey, val: number) => {
        setOccupancyBoxes(prev => prev.map(b => (b.id === id ? { ...b, [key]: val } : b)));
    }, []);

    const handleSensorDrag = useCallback((xOffset: number, newY1: number) => {
        setInputs(prev => ({ ...prev, sensorXOffset: xOffset, y1: newY1 }));
    }, []);

    const handleReset = useCallback(() => {
        setInputs(defaultInputsForMode(mode));
        setCustomCuboids([]);
        setHasCLICuboids(false);
        if (mode === 'IntruderDetection') {
            setOccupancyBoxes(DEFAULT_OCCUPANCY_BOXES);
        }
    }, [mode]);

    // Compute output
    const output = computeZones({ ...inputs, mode }, occupancyBoxes);

    // For SBR/CPDLPD: merge computed + custom, with custom overriding computed for same seatIdx+zoneIdx
    // This way CLI updates replace specific cuboids while keeping others from computed
    let mergedCuboids: Cuboid[];
    if (mode !== 'IntruderDetection') {
        const customMap = new Map(customCuboids.map(c => [`${c.seatIdx}-${c.zoneIdx}`, c]));
        mergedCuboids = output.cuboids.map(c => {
            const key = `${c.seatIdx}-${c.zoneIdx}`;
            return customMap.has(key) ? customMap.get(key)! : c;
        });
        // Add any custom cuboids that don't match computed ones (new additions)
        customCuboids.forEach(c => {
            const key = `${c.seatIdx}-${c.zoneIdx}`;
            if (!output.cuboids.some(oc => oc.seatIdx === c.seatIdx && oc.zoneIdx === c.zoneIdx)) {
                mergedCuboids.push(c);
            }
        });
    } else {
        mergedCuboids = output.cuboids;
    }

    const mergedOutput = { ...output, cuboids: mergedCuboids };

    const outputText = formatOutput(mergedOutput);
    const configText = buildConfigFile(mergedOutput);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(configText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [configText]);

    const cliTextareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [cliInput, setCliInput] = useState('');
    const [cliStatus, setCliStatus] = useState<{ message: string; type: 'success' | 'error' | null }>({ message: '', type: null });

    const handleCLIPaste = useCallback(() => {
        // Read directly from ref to avoid stale state
        const text = cliTextareaRef.current?.value.trim() ?? cliInput.trim();
        if (!text) {
            setCliStatus({ message: 'No commands to parse', type: 'error' });
            return;
        }

        // Parse with current state
        const result = parseCLICommands(text, inputs, occupancyBoxes, customCuboids, mode);

        if (result.parsedCount === 0) {
            setCliStatus({ message: 'No valid commands found for this mode', type: 'error' });
            return;
        }

        setInputs(result.inputs);
        setOccupancyBoxes(result.occupancyBoxes);
        setCustomCuboids(result.customCuboids);

        // Track if CLI cuboidDef commands were applied (for SBR/CPDLPD)
        const hasCuboidDef = text.toLowerCase().includes('cuboiddef');
        setHasCLICuboids(hasCuboidDef && (mode === 'SBR' || mode === 'CPDLPD'));

        setCliStatus({ message: `Successfully parsed ${result.parsedCount} command(s)`, type: 'success' });
        setCliInput(''); // Clear input after successful parse
        if (cliTextareaRef.current) {
            cliTextareaRef.current.value = '';
        }
    }, [inputs, occupancyBoxes, customCuboids, mode]);

    return (
        <div className="app">
            <header className="app-header">
                <span className="header-title">Zone Definition Generator</span>
                <button className="header-help-btn" onClick={() => setShowReadme(true)}>Quick Start Guide</button>
                <button className="header-help-btn" onClick={() => setShowMeasurementImage(true)}>Zone Measurements</button>
                <div className="mode-selector">
                    <span className="mode-label">Mode:</span>
                    <button
                        className={'mode-btn' + (mode === 'SBR' ? ' active' : '')}
                        onClick={() => handleModeChange('SBR')}
                    >SBR</button>
                    <button
                        className={'mode-btn' + (mode === 'CPDLPD' ? ' active' : '')}
                        onClick={() => handleModeChange('CPDLPD')}
                    >CPD/LPD</button>
                    <button
                        className={'mode-btn' + (mode === 'IntruderDetection' ? ' active' : '')}
                        onClick={() => handleModeChange('IntruderDetection')}
                    >Intruder Detection</button>
                    <button className="reset-btn" onClick={handleReset}>Reset Defaults</button>
                </div>
            </header>

            <div className="main-layout">
                <aside className="inputs-panel">
                    {/* CLI Command Paste Section */}
                    <div className="cli-section">
                        <h3 className="cli-title">Load CLI Commands</h3>
                        <div className="cli-input-wrapper">
                            <textarea
                                ref={cliTextareaRef}
                                className="cli-textarea"
                                value={cliInput}
                                onChange={(e) => setCliInput(e.target.value)}
                                placeholder={mode === 'IntruderDetection'
                                    ? "Paste CLI commands here (e.g., sensorPosition 0 0.5 1.1 0 -60\noccupancyBox 1 0.0 0.55 -0.8 -0.2 0.5 1.15)"
                                    : "Paste CLI commands here (e.g., sensorPosition 0 0.5 1.1 0 -60\ncuboidDef 0 0 0.15 0.7 0.5 0.9 0.6 1.2)"}
                                rows={4}
                                spellCheck={false}
                            />
                            <div className="cli-buttons">
                                <button className="cli-btn-primary" onClick={handleCLIPaste}>
                                    Apply Commands
                                </button>
                                <button className="cli-btn-secondary" onClick={() => setCliInput('')}>
                                    Clear
                                </button>
                            </div>
                        </div>
                        {cliStatus.message && (
                            <div className={`cli-status ${cliStatus.type}`}>
                                {cliStatus.message}
                            </div>
                        )}
                        <details className="cli-help">
                            <summary>Supported Commands for {mode} Mode</summary>
                            <div className="cli-help-content">
                                <pre>{mode === 'IntruderDetection' ? `sensorPosition xOffset yOffset zOffset azimTilt elevTilt
  Example: sensorPosition 0 0.5 1.1 0 -60

occupancyBox id xMin xMax yMin yMax zMin zMax
  Example: occupancyBox 1 0.0 0.55 -0.8 -0.2 0.5 1.15

% or # for comments` : `sensorPosition xOffset yOffset zOffset azimTilt elevTilt
  Example: sensorPosition 0 0.5 1.1 0 -60

cuboidDef seatIdx zoneIdx xMin xMax yMin yMax zMin zMax
  Example: cuboidDef 0 0 0.15 0.7 0.5 0.9 0.6 1.2
  (Adds/overrides a custom cuboid zone)

% or # for comments`}</pre>
                            </div>
                        </details>
                    </div>

                    <FieldGroup title="Sensor" fields={SENSOR_FIELDS} inputs={inputs} onChange={handleChange} />
                    {mode === 'IntruderDetection' ? null : (
                        <>
                            <FieldGroup title="Vehicle" fields={VEHICLE_FIELDS} inputs={inputs} onChange={handleChange} />
                            <FieldGroup title="Row 1 Seats" fields={SEAT1_FIELDS} inputs={inputs} onChange={handleChange} />
                            <FieldGroup title="Row 2 Seats" fields={SEAT2_FIELDS} inputs={inputs} onChange={handleChange} />
                            <FieldGroup title="Expand Margins" fields={MARGIN_FIELDS} inputs={inputs} onChange={handleChange} />
                            <FieldGroup title="Middle Seat Tuning" fields={MIDDLE_FIELDS} inputs={inputs} onChange={handleChange} />
                        </>
                    )}
                </aside>

                <main className="right-panel">
                    {/* 3D Visualizer */}
                    <ZoneVisualizer
                        output={mergedOutput}
                        mode={mode}
                        ceilingHeight={inputs.ceilingHeight}
                        yRef={inputs.yRef}
                        y1={inputs.y1}
                        y2={inputs.y2}
                        y3={inputs.y3}
                        theta={inputs.theta}
                        omega={inputs.omega}
                        onSensorDrag={handleSensorDrag}
                    />

                    {/* Zone table + collapsible config */}
                    <div className="lower-section">
                        <div className="output-header">
                            <h2 className="output-title">
                                {mode === 'IntruderDetection' ? 'Occupancy Box Definitions' : 'Computed Zone Definitions'}
                            </h2>
                            <button className="copy-btn" onClick={handleCopy}>
                                {copied ? 'Copied!' : 'Copy CLI Commands'}
                            </button>
                        </div>

                        <div className="table-wrap">
                            {mode === 'IntruderDetection' ? (
                                <table className="output-table box-table">
                                    <thead>
                                        <tr>
                                            <th>Occupancy Box</th>
                                            <th>xMin</th>
                                            <th>xMax</th>
                                            <th>yMin</th>
                                            <th>yMax</th>
                                            <th>zMin</th>
                                            <th>zMax</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {occupancyBoxes.map(b => (
                                            <OccupancyBoxRow key={b.id} box={b} onChange={handleBoxChange} />
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="output-table">
                                    <thead>
                                        <tr>
                                            <th>Seat</th>
                                            <th>Zone</th>
                                            <th>xMin</th>
                                            <th>xMax</th>
                                            <th>yMin</th>
                                            <th>yMax</th>
                                            <th>zMin</th>
                                            <th>zMax</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <SensorPositionRow sp={output.sensorPosition} />
                                        {output.cuboids.map(c => (
                                            <CuboidRow key={c.seatIdx + '-' + c.zoneIdx} c={c} />
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <details className="config-details">
                            <summary className="config-summary">CLI Commands ({mode} mode)</summary>
                            <pre className="output-text">{configText}</pre>
                        </details>
                    </div>
                </main>
            </div>

            {/* README Modal */}
            {showReadme && (
                <div className="readme-modal-overlay" onClick={() => setShowReadme(false)}>
                    <div className="readme-modal" onClick={e => e.stopPropagation()}>
                        <div className="readme-header">
                            <h2>{README_CONTENT.title}</h2>
                            <button className="readme-close" onClick={() => setShowReadme(false)}>×</button>
                        </div>
                        <div className="readme-content">
                            {README_CONTENT.sections.map((section, idx) => (
                                <div key={idx} className="readme-section">
                                    <h3>{section.title}</h3>
                                    {section.content && <p>{section.content}</p>}
                                    {section.items && (
                                        <ul>
                                            {section.items.map((item, i) => (
                                                <li key={i}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="readme-footer">
                            <label>
                                <input
                                    type="checkbox"
                                    onChange={e => {
                                        if (e.target.checked) localStorage.setItem('zoneDefGen_readme_dismissed', 'true');
                                        else localStorage.removeItem('zoneDefGen_readme_dismissed');
                                    }}
                                />
                                Don't show again
                            </label>
                            <button className="readme-got-it" onClick={() => setShowReadme(false)}>Got It</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Zone Measurements Image Modal */}
            {showMeasurementImage && (
                <div className="readme-modal-overlay" onClick={() => setShowMeasurementImage(false)}>
                    <div className="image-modal" onClick={e => e.stopPropagation()}>
                        <div className="readme-header">
                            <h2>Zone Measurements Reference</h2>
                            <button className="readme-close" onClick={() => setShowMeasurementImage(false)}>×</button>
                        </div>
                        <div className="image-modal-content">
                            <img src={ZONE_MEASUREMENTS_IMAGE} alt="Zone measurements reference diagram" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
